import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { selectLoginResponse } from '../../redux/login/login.selector';
import Icon from '../../components/icon/Icon';
import {
	getCompOffReportStart,
	approveCompOffByHRStart,
	approveCompOffByHRResponseRestStart,
} from '../../redux/NewReport/NewReport.actions';
import {
	selectCompOffReportResponse,
	selectApproveCompOffByHRResponse,
} from '../../redux/NewReport/NewReport.selectors';
import Input from '../../components/bootstrap/forms/Input';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Label from '../../components/bootstrap/forms/Label';
import showNotification from '../../components/extras/showNotification';
import Swal from 'sweetalert2';
interface CompOffRecord {
	Employee_ID: string;
	Employee_Name: string;
	Designation: string;
	branchName: string;
	Work_Date: string;
	Valid_Till: string;
	Reason: string;
	Status: string;
	Approved_By: string | null;
	Rejected_Reason: string | null;
	Created_On: string;
	Status_Label: string;
	Is_Used: number;
	Used_On: string | null;
	compOffID: number;
}

const CompOffReportPage = () => {
	const dispatch = useDispatch();
	const loginData = useSelector(selectLoginResponse);
    const compOffResp = useSelector(selectCompOffReportResponse);
    const compOffReportData = compOffResp ?? null;
	const approveCompOffByHRResp = useSelector(selectApproveCompOffByHRResponse);

	const [startDate, setStartDate] = useState<string>('');
	const [endDate, setEndDate] = useState<string>('');
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [selectedCompOffID, setSelectedCompOffID] = useState<number | null>(null);
	const [rejectionReason, setRejectionReason] = useState<string>('');

	// Set default dates (current month)
	useEffect(() => {
		const today = new Date();
		const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
		const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
		
		setStartDate(firstDay.toISOString().split('T')[0]);
		setEndDate(lastDay.toISOString().split('T')[0]);
	}, []);

	// Auto-fetch report on mount and when dates change
	useEffect(() => {
		if (startDate && endDate && loginData?.data?.organisationID) {
			handleFetchReport();
		}
	}, [startDate, endDate, loginData?.data?.organisationID]);

	// Handle approval response
	useEffect(() => {
		if (approveCompOffByHRResp) {
			const response = approveCompOffByHRResp;
			if (response.status === 'success') {
				Swal.fire({
					title: 'Success',
					text: response.message_text || 'Comp off request processed successfully',
					icon: 'success',
				});
				// Refresh the report
				handleFetchReport();
				dispatch(approveCompOffByHRResponseRestStart());
			} else if (response.status === 'error') {
				Swal.fire({
					title: 'Error',
					text: response.message_text || 'Failed to process comp off request',
					icon: 'error',
				});
				dispatch(approveCompOffByHRResponseRestStart());
			}
		}
	}, [approveCompOffByHRResp, dispatch]);

	// Handle fetch report
	const handleFetchReport = () => {
		if (!startDate || !endDate) {
			showNotification(
				'Warning',
				'Please select both start and end dates',
				'warning'
			);
			return;
		}

    dispatch(getCompOffReportStart({
            organisationID: loginData?.data?.organisationID || 1,
            startDate: startDate,
            endDate: endDate,
    }));
	};

	// Get status badge color
	const getStatusBadgeColor = (status: string, isUsed: number) => {
		if (isUsed === 1) return 'success';
		switch (status) {
			case 'Yet To Be Approved':
				return 'warning';
			case 'Manager Approved':
				return 'info';
			case 'Pending HR Approval':
				return 'info';
			case 'Approved':
				return 'success';
			case 'Rejected':
				return 'danger';
			case 'Rejected by HR':
				return 'danger';
			default:
				return 'secondary';
		}
	};

	// Handle approve comp off
	const handleApprove = async (compOffID: number) => {
		const result = await Swal.fire({
			title: 'Approve Comp Off?',
			text: 'Are you sure you want to approve this compensatory off request?',
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes, Approve',
			cancelButtonText: 'Cancel',
		});

		if (result.isConfirmed) {
			const payload = {
				compOffID: compOffID,
				action: 'approve',
				approvedBy: loginData?.data?.employeeName || 'HR',
			};

			// Payload will be encrypted in the saga
			dispatch(approveCompOffByHRStart(payload));
		}
	};

	// Handle reject comp off
	const handleReject = (compOffID: number) => {
		setSelectedCompOffID(compOffID);
		setRejectionReason('');
		setShowRejectModal(true);
	};

	// Submit rejection
	const handleSubmitRejection = async () => {
		if (!rejectionReason.trim()) {
			showNotification('Warning', 'Please provide a rejection reason', 'warning');
			return;
		}

		if (!selectedCompOffID) return;

		const payload = {
			compOffID: selectedCompOffID,
			action: 'reject',
			rejectionReason: rejectionReason.trim(),
		};

		// Payload will be encrypted in the saga
		dispatch(approveCompOffByHRStart(payload));
		setShowRejectModal(false);
		setSelectedCompOffID(null);
		setRejectionReason('');
	};

	// Export to Excel
	const handleExportToExcel = () => {
		const data = compOffReportData?.data || [];
		if (data.length === 0) {
			showNotification('Warning', 'No data to export', 'warning');
			return;
		}

		// Create CSV content
		const headers = [
			'Employee ID',
			'Employee Name',
			'Designation',
			'Branch',
			'Work Date',
			'Valid Till',
			'Reason',
			'Status',
			'Approved By',
			'Is Used',
			'Used On',
			'Created On',
		];
		
		const csvRows = [headers.join(',')];
		
		data.forEach((record: CompOffRecord) => {
			const row = [
				record.Employee_ID,
				`"${record.Employee_Name}"`,
				`"${record.Designation}"`,
				`"${record.branchName}"`,
				record.Work_Date,
				record.Valid_Till || '',
				`"${record.Reason || ''}"`,
				record.Status_Label,
				record.Approved_By || '',
				record.Is_Used === 1 ? 'Yes' : 'No',
				formatDateOnly(record.Used_On || ''),
				formatDateOnly(record.Created_On),
			];
			csvRows.push(row.join(','));
		});

		// Download
		const csvString = csvRows.join('\n');
		const blob = new Blob([csvString], { type: 'text/csv' });
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `CompOff_Report_${startDate}_to_${endDate}.csv`;
		a.click();
		window.URL.revokeObjectURL(url);
	};

	const formatDateOnly = (value: string | null): string => {
		if (!value) return '-';
		// Try splitting on space or 'T'
		const parts = value.includes(' ') ? value.split(' ') : value.split('T');
		if (parts[0] && /^\d{4}-\d{2}-\d{2}$/.test(parts[0])) return parts[0];
		// Fallback: return as-is
		return value;
	};

	return (
		<PageWrapper title='CompOff Report'>
			<SubHeader>
				<SubHeaderLeft>
					<Icon icon='Description' className='me-2' size='2x' />
					<span className='fs-4'>Compensatory Off (CompOff) Report</span>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<Card stretch>
					<CardHeader>
						<CardLabel icon='CalendarToday' iconColor='primary'>
							<CardTitle>CompOff Report</CardTitle>
						</CardLabel>
					</CardHeader>
					<CardBody>
						<div className='row g-3 mb-4'>
							<div className='col-md-3'>
								<FormGroup>
									<Label htmlFor='startDate'>Start Date</Label>
									<Input
										type='date'
										id='startDate'
										value={startDate}
										onChange={(e: any) => setStartDate(e.target.value)}
									/>
								</FormGroup>
							</div>
							<div className='col-md-3'>
								<FormGroup>
									<Label htmlFor='endDate'>End Date</Label>
									<Input
										type='date'
										id='endDate'
										value={endDate}
										onChange={(e: any) => setEndDate(e.target.value)}
									/>
								</FormGroup>
							</div>
							<div className='col-md-3 d-flex align-items-end'>
								<Button
									color='primary'
									icon='Search'
									onClick={handleFetchReport}>
									Fetch Report
								</Button>
							</div>
							<div className='col-md-3 d-flex align-items-end justify-content-end'>
								<Button
									color='success'
									icon='FileDownload'
									onClick={handleExportToExcel}
									isDisable={!compOffReportData?.data || compOffReportData.data.length === 0}>
									Export to Excel
								</Button>
							</div>
						</div>

						{compOffReportData && compOffReportData.status === 'success' && (
							<div className='table-responsive'>
								<table className='table table-modern table-hover'>
									<thead>
										<tr>
											<th>S.No</th>
											<th>Emp ID</th>
											<th>Employee Name</th>
											<th>Designation</th>
											<th>Branch</th>
											<th>Work Date</th>
											<th>Valid Till</th>
											<th>Reason</th>
											<th>Status</th>
											<th>Used</th>
									<th>Used On</th>
											<th>Created On</th>
											<th>Actions</th>
										</tr>
									</thead>
									<tbody>
										{compOffReportData.data && compOffReportData.data.length > 0 ? (
											compOffReportData.data.map((record: CompOffRecord, index: number) => (
												<tr key={record.compOffID}>
													<td>{index + 1}</td>
													<td>{record.Employee_ID}</td>
													<td>{record.Employee_Name}</td>
													<td>{record.Designation}</td>
													<td>{record.branchName}</td>
													<td>{record.Work_Date}</td>
													<td>{record.Valid_Till || '-'}</td>
													<td className='text-truncate' style={{ maxWidth: '200px' }} title={record.Reason}>
														{record.Reason || '-'}
													</td>
													<td>
														<span className={`badge bg-${getStatusBadgeColor(record.Status, record.Is_Used)}`}>
															{record.Status_Label}
														</span>
													</td>
													<td>
														{record.Is_Used === 1 ? (
															<span className='badge bg-success'>
																<Icon icon='CheckCircle' /> Yes
															</span>
														) : (
															<span className='badge bg-secondary'>No</span>
														)}
													</td>
											<td>{formatDateOnly(record.Used_On)}</td>
											<td>{formatDateOnly(record.Created_On)}</td>
													<td>
														{record.Status === 'Manager Approved' && record.Is_Used === 0 ? (
															<div className='d-flex gap-2'>
																<Button
																	color='success'
																	size='sm'
																	icon='CheckCircle'
																	onClick={() => handleApprove(record.compOffID)}
																>
																	Approve
																</Button>
																<Button
																	color='danger'
																	size='sm'
																	icon='Cancel'
																	onClick={() => handleReject(record.compOffID)}
																>
																	Reject
																</Button>
															</div>
														) : (
															<span className='text-muted'>-</span>
														)}
													</td>
												</tr>
											))
										) : (
											<tr>
												<td colSpan={12} className='text-center'>
													No comp off records found for the selected date range
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						)}

						{compOffReportData && compOffReportData.status === 'error' && (
							<div className='alert alert-danger'>
								<Icon icon='Warning' className='me-2' />
								{compOffReportData.message || 'Failed to fetch comp off report'}
							</div>
						)}
					</CardBody>
				</Card>

				{/* Rejection Modal */}
				{showRejectModal && (
					<div className='modal show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
						<div className='modal-dialog modal-dialog-centered'>
							<div className='modal-content'>
								<div className='modal-header'>
									<h5 className='modal-title'>Reject Comp Off Request</h5>
									<button
										type='button'
										className='btn-close'
										onClick={() => {
											setShowRejectModal(false);
											setSelectedCompOffID(null);
											setRejectionReason('');
										}}
									></button>
								</div>
								<div className='modal-body'>
									<FormGroup>
										<Label htmlFor='rejectionReason'>Rejection Reason *</Label>
										<textarea
											className='form-control'
											id='rejectionReason'
											rows={4}
											value={rejectionReason}
											onChange={(e: any) => setRejectionReason(e.target.value)}
											placeholder='Please provide a reason for rejection...'
										/>
									</FormGroup>
								</div>
								<div className='modal-footer'>
									<Button
										color='secondary'
										onClick={() => {
											setShowRejectModal(false);
											setSelectedCompOffID(null);
											setRejectionReason('');
										}}
									>
										Cancel
									</Button>
									<Button
										color='danger'
										onClick={handleSubmitRejection}
										isDisable={!rejectionReason.trim()}
									>
										Reject
									</Button>
								</div>
							</div>
						</div>
					</div>
				)}
			</Page>
		</PageWrapper>
	);
};

export default CompOffReportPage;
